import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { fileUrl } from "@/lib/api";
import {
  House, MagnifyingGlass, Buildings, UsersThree, CalendarDots, Graph, Flower,
  FileText, UserCircle, SignOut, GraduationCap, List, X, Robot, Detective, Sparkle, ArrowRight, CaretDown,
} from "@phosphor-icons/react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/NotificationBell";
import GlobalSearch from "@/components/GlobalSearch";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const PRIMARY = [
  { to: "/dashboard", label: "Home", icon: House },
  { to: "/institutions", label: "Institutions", icon: Buildings },
  { to: "/groups", label: "Communities", icon: UsersThree },
  { to: "/events", label: "Events", icon: CalendarDots },
];
const MORE = [
  { to: "/directory", label: "Directory", icon: MagnifyingGlass },
  { to: "/find", label: "Reconnect", icon: Detective },
  { to: "/network", label: "Network", icon: Graph },
  { to: "/memorials", label: "In Memoriam", icon: Flower },
  { to: "/faculty-networks", label: "Faculty", icon: GraduationCap },
  { to: "/resumes", label: "Resume", icon: FileText },
];
const ADMIN = [
  { to: "/admin/crawler", label: "Crawler", icon: Robot },
  { to: "/admin/content", label: "Content", icon: FileText },
  { to: "/admin/site-content", label: "CMS", icon: Sparkle },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  if (!user) return null;
  const initials = (user.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-border/60 bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-5 py-3">
        <Link to="/dashboard" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
          <span className="relative block h-9 w-11 shrink-0 overflow-hidden"><img src="/alumnipulse-logo.png" alt="AlumniPulse" className="absolute left-1/2 top-0 max-w-none -translate-x-1/2" style={{ height: "66px" }} /></span>
          <span className="font-heading text-lg font-semibold tracking-tight hidden sm:block">AlumniPulse™</span>
        </Link>

        <nav className="ml-2 hidden items-center gap-1 md:flex" data-testid="app-nav">
          {PRIMARY.map((l) => {
            const active = loc.pathname === l.to;
            return (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex items-center gap-2 rounded-full px-3 py-2 text-sm transition-colors ${active ? "bg-accent text-accent-foreground font-medium" : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"}`}>
                <l.icon size={17} weight={active ? "fill" : "regular"} /> {l.label}
              </Link>
            );
          })}
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="nav-more" className="flex items-center gap-1 rounded-full px-3 py-2 text-sm text-muted-foreground outline-none transition-colors hover:bg-accent/60 hover:text-foreground data-[state=open]:bg-accent data-[state=open]:text-foreground">
              More <CaretDown size={12} weight="bold" className="opacity-70" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-52">
              {MORE.map((l) => (
                <DropdownMenuItem key={l.to} onClick={() => nav(l.to)} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <l.icon className="mr-2" size={16} /> {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {user.role === "admin" && (
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="nav-admin-menu" className="hidden items-center gap-1 rounded-full px-3 py-2 text-sm text-muted-foreground outline-none transition-colors hover:bg-accent/60 hover:text-foreground data-[state=open]:bg-accent md:flex">
                <Robot size={16} /> Admin <CaretDown size={12} weight="bold" className="opacity-70" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {ADMIN.map((l) => (
                  <DropdownMenuItem key={l.to} onClick={() => nav(l.to)} data-testid={`nav-admin-${l.label.toLowerCase()}`}>
                    <l.icon className="mr-2" size={16} /> {l.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <GlobalSearch className="hidden sm:grid" />
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-full border border-border/60 py-1 pl-1 pr-3 hover:bg-accent/60 transition-colors" data-testid="nav-user-menu">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={fileUrl(user.avatar_file_id)} />
                  <AvatarFallback className="bg-secondary text-secondary-foreground text-xs">{initials}</AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium hidden sm:block max-w-[120px] truncate">{user.name}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={() => nav("/profile")} data-testid="menu-profile"><UserCircle className="mr-2" size={16} /> My Profile</DropdownMenuItem>
              <DropdownMenuItem onClick={() => nav("/connections")} data-testid="menu-connections"><UsersThree className="mr-2" size={16} /> Connections</DropdownMenuItem>
              <DropdownMenuItem onClick={() => { try { localStorage.removeItem("ap_onboarding_dismissed"); } catch (e) { /* ignore */ } nav("/dashboard"); }} data-testid="menu-getting-started"><Sparkle className="mr-2" size={16} /> Getting started</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { logout(); nav("/"); }} data-testid="menu-logout"><SignOut className="mr-2" size={16} /> Sign out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X size={20} /> : <List size={20} />}
          </Button>
        </div>
      </div>
      {open && (
        <nav className="border-t border-border/60 px-4 py-2 md:hidden">
          <div className="mb-2 pt-1"><GlobalSearch trigger="full" /></div>
          {[...PRIMARY, ...MORE, ...(user.role === "admin" ? ADMIN : [])].map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
              className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-foreground hover:bg-accent">
              <l.icon size={18} /> {l.label}
            </Link>
          ))}
        </nav>
      )}
    </header>
    </>
  );
}
